import type { ChainLinkView } from '@concord/domain/contracts';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { cn } from '../cn';
import { Icon } from '../icons';
import s from './case.module.css';

const KIND: Record<ChainLinkView['kind'], string> = {
  customer: 'Customer',
  plan: 'Plan',
  pool: 'Network',
  funding: 'Funding',
};

/** The entitlement chain on the case header (CON-06). A broken or thin link is marked. */
export function EntitlementChain({
  links,
  note,
  planUntil,
}: {
  links: ChainLinkView[];
  note?: ReactNode;
  /** "Dec 31, 2026": shown on the plan link as in the prototype. */
  planUntil?: string | null;
}) {
  return (
    <div className={s.chain}>
      {links.map((l, i) => (
        <span key={l.kind} style={{ display: 'contents' }}>
          <span className={s.lk} title={l.detail ?? undefined}>
            <span
              className={l.status === 'ok' ? s.ok : s.warn}
              style={l.status === 'broken' ? { background: 'var(--bad)' } : undefined}
            />
            <span className={s.k}>{KIND[l.kind]}</span>
            {l.label ?? 'Missing'}
            {l.kind === 'plan' && l.status === 'ok' && planUntil ? ` · to ${planUntil}` : ''}
            {l.detail && l.status !== 'ok'
              ? ` · ${l.detail}`
              : l.kind === 'pool' && l.detail
                ? ` · ${l.detail}`
                : ''}
          </span>
          {i < links.length - 1 && <span className={s.arrow}>›</span>}
        </span>
      ))}
      {note && (
        <span className="sub2" style={{ marginLeft: '.4rem' }}>
          {note}
        </span>
      )}
    </div>
  );
}

export interface Counter {
  key: string;
  label: string;
  value: ReactNode;
  small?: ReactNode;
  tone?: 'ok' | 'bad' | 'wait';
  compact?: boolean;
}

/** Panel counters, visible without a dialog (CON-07). */
export function Counters({ items }: { items: Counter[] }) {
  return (
    <section className="panel" aria-label="Panel counters">
      <div className={s.counters} style={{ '--n': items.length } as CSSProperties}>
        {items.map((c) => (
          <div key={c.key}>
            <span className="key">{c.label}</span>
            <span
              className={cn(s.v, c.tone && s[c.tone])}
              style={c.compact ? { fontSize: '.95rem', paddingTop: '.3rem' } : undefined}
            >
              {c.value} {c.small && <small>{c.small}</small>}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ClinicalFacts({
  items,
}: {
  items: { key: string; label: string; value: ReactNode; sub?: ReactNode }[];
}) {
  return (
    <div className={s.clin}>
      {items.map((f) => (
        <div key={f.key} className={s.f}>
          <span className={cn('key', s.k)}>{f.label}</span>
          <span className={s.v}>
            {f.value}
            {f.sub && <span className="sub2">{f.sub}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Prose({ children }: { children: ReactNode }) {
  return <div className={s.prose}>{children}</div>;
}

export function ProseHeading({ children, first }: { children: ReactNode; first?: boolean }) {
  return <h3 style={first ? undefined : { marginTop: '1.1rem' }}>{children}</h3>;
}

/** Numbered questions, as the physicians see them (INT-06). */
export function NumberedQuestions({ questions }: { questions: string[] }) {
  return (
    <ol className={s.qs}>
      {questions.map((q) => (
        <li key={q}>{q}</li>
      ))}
    </ol>
  );
}

export function MetaStrip({ items }: { items: ReactNode[] }) {
  return (
    <div className={s.meta}>
      {items.map((m, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static positional strip
        <span key={i}>{m}</span>
      ))}
    </div>
  );
}

/** One record row: file type, description, facts, action (DOC-05). */
export function FileRow({
  ext,
  name,
  meta,
  action,
}: {
  ext: string;
  name: ReactNode;
  meta: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={s.file}>
      <span className={s.ic}>{ext}</span>
      <div className={s.b}>
        <div className={s.n1}>{name}</div>
        <div className={s.n2}>{meta}</div>
      </div>
      {action}
    </div>
  );
}

export function FileList({ children, lockNote }: { children: ReactNode; lockNote?: ReactNode }) {
  return (
    <>
      <div className={s.files}>{children}</div>
      {lockNote && (
        <div className={s.lock}>
          <Icon.Lock />
          {lockNote}
        </div>
      )}
    </>
  );
}

export function DownloadLink({ href, children = 'Download' }: { href: string; children?: ReactNode }) {
  return (
    <a className={s.dl} href={href}>
      {children}
    </a>
  );
}

/* ---------- participants ---------- */

export function PeopleGroup({ title, right }: { title: ReactNode; right?: ReactNode }) {
  return (
    <div className={s.grp}>
      {title}
      {right && <span className={s.r}>{right}</span>}
    </div>
  );
}

export function PersonRow({
  initials,
  physician,
  name,
  tag,
  detail,
  status,
}: {
  initials: string;
  physician?: boolean;
  name: ReactNode;
  tag?: ReactNode;
  detail: ReactNode;
  status?: ReactNode;
}) {
  return (
    <div className={s.person}>
      <span className={cn(s.av, physician && s.md)}>{initials}</span>
      <div className={s.b}>
        <div className={s.n1}>
          {name} {tag && <span className={s.tag}>{tag}</span>}
        </div>
        <div className={s.n2}>{detail}</div>
      </div>
      {status}
    </div>
  );
}

export function People({ children }: { children: ReactNode }) {
  return <div className={s.people}>{children}</div>;
}

/* ---------- consultation ---------- */

export function Schedule({ children }: { children: ReactNode }) {
  return <div className={s.sched}>{children}</div>;
}
export const ScheduleBig = ({ children }: { children: ReactNode }) => <div className={s.big}>{children}</div>;
export const ScheduleRow = ({ children }: { children: ReactNode }) => <div className={s.row}>{children}</div>;
export const ScheduleNote = ({ children }: { children: ReactNode }) => <div className={s.tz}>{children}</div>;

/* ---------- event log (CON-05, CON-09, CON-10) ---------- */

export interface LogEntry {
  id: string | number;
  day: string;
  time: string;
  zone: string;
  dot?: 'ok' | 'bad' | 'acc' | 'wait' | 'alert';
  title: ReactNode;
  who?: ReactNode;
  detail?: ReactNode;
  note?: boolean;
  staffOnly?: boolean;
}

export function EventLog({
  entries,
  sub,
  onlyParticipantsToggle = true,
}: {
  entries: LogEntry[];
  sub?: ReactNode;
  onlyParticipantsToggle?: boolean;
}) {
  const [participantView, setParticipantView] = useState(false);
  const shown = participantView ? entries.filter((e) => !e.staffOnly) : entries;
  const days: { day: string; items: LogEntry[] }[] = [];
  for (const e of shown) {
    const last = days.at(-1);
    if (last && last.day === e.day) last.items.push(e);
    else days.push({ day: e.day, items: [e] });
  }
  return (
    <section className="panel" aria-label="Event log">
      <div className="phead">
        <h2>Event log</h2>
        {sub && <span className="sub">{sub}</span>}
        {onlyParticipantsToggle && (
          <div className={s.logtools}>
            <button
              type="button"
              className={s.toggle}
              aria-pressed={participantView}
              onClick={() => setParticipantView((v) => !v)}
            >
              <span className={s.sw} />
              Show as participants see it
            </button>
          </div>
        )}
      </div>
      {days.map((d) => (
        <div key={d.day}>
          <div className={s.daysep}>{d.day}</div>
          <ol className={s.log}>
            {d.items.map((e) => (
              <li key={e.id} className={cn(s.ev, e.note && s.note)}>
                <span className={s.t}>
                  <b>{e.time}</b>
                  {e.zone}
                </span>
                <span className={cn(s.dot, e.dot && s[e.dot])} />
                <div className={s.b}>
                  {e.note && <span className={s.lab}>Internal note · staff only</span>}
                  {e.title}
                  {e.who && <span className={s.who}>{e.who}</span>}
                  {e.detail && <div className={s.d}>{e.detail}</div>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
      {!shown.length && <div className={s.empty}>Nothing logged yet.</div>}
    </section>
  );
}

/* ---------- layout ---------- */

export function CaseLayout({ main, side }: { main: ReactNode; side: ReactNode }) {
  return (
    <div className={s.layout}>
      <div className={s.col}>{main}</div>
      <div className={s.col}>{side}</div>
    </div>
  );
}

export { s as caseStyles };
